/**
 * Supabase Query Builder Utilities
 * Reusable functions for building Supabase queries with common patterns
 */

import { SupabaseClient } from "@supabase/supabase-js";

export interface DateFilterOptions {
  dateField: string;
  fromDate?: Date | null;
  toDate?: Date | null;
}

export interface PaginationOptions {
  limit?: number;
  page?: number;
}

/**
 * Apply date filtering to a Supabase query
 */
export function applyDateFilter(
  query: any,
  options: DateFilterOptions
): any {
  const { dateField, fromDate, toDate } = options;
  
  if (fromDate) {
    query = query.gte(dateField, fromDate.toISOString());
  }
  
  if (toDate) {
    query = query.lte(dateField, toDate.toISOString());
  }
  
  return query;
}

/**
 * Apply pagination to a Supabase query
 */
export function applyPagination(
  query: any,
  options: PaginationOptions
): any {
  const { limit, page } = options;
  
  if (limit) {
    const offset = page ? (page - 1) * limit : 0;
    query = query.range(offset, offset + limit - 1);
  }
  
  return query;
}

/**
 * Build contact messages query with date filtering
 */
export function buildContactMessagesQuery(
  supabase: SupabaseClient,
  contactId: string,
  fromDate?: Date | null,
  toDate?: Date | null,
  limit?: number
) {
  let query = supabase
    .from("contact_messages")
    .select(`
      id,
      subject,
      body,
      message_type,
      direction,
      status,
      sent_at,
      delivered_at,
      read_at,
      external_message_id,
      created_at,
      updated_at,
      user_id
    `)
    .eq("contact_id", contactId);

  // Apply date filtering
  query = applyDateFilter(query, {
    dateField: "sent_at",
    fromDate,
    toDate,
  });

  // Apply ordering and pagination
  query = query.order("sent_at", { ascending: false });
  
  if (limit) {
    query = query.limit(limit);
  }

  return query;
}

/**
 * Build communications query with date filtering
 */
export function buildCommunicationsQuery(
  supabase: SupabaseClient,
  contactId: string,
  fromDate?: Date | null,
  toDate?: Date | null,
  limit?: number
) {
  let query = supabase
    .from("communications")
    .select(`
      id,
      title,
      body,
      direction,
      status,
      occurred_at,
      created_at,
      updated_at,
      user_id,
      type_id,
      communication_types (
        key,
        label
      )
    `)
    .eq("contact_id", contactId);

  // Apply date filtering
  query = applyDateFilter(query, {
    dateField: "occurred_at",
    fromDate,
    toDate,
  });

  // Apply ordering and pagination
  query = query.order("occurred_at", { ascending: false });
  
  if (limit) {
    query = query.limit(limit);
  }

  return query;
}

/**
 * Build time entries query with date filtering and relationships
 */
export function buildTimeEntriesQuery(
  supabase: SupabaseClient,
  options: {
    ticketId?: string;
    userId?: string;
    contactId?: string;
    entryType?: string;
    isApproved?: boolean;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    limit?: number;
    page?: number;
    search?: string;
    syncStatus?: string;
    hours?: number;
  } = {}
) {
  let query = supabase.from("time_entries").select(`
    *,
    ticket:tickets(
      id,
      ticket_number,
      title,
      contact_id,
      contact:contacts(
        id,
        name,
        company_name,
        email
      )
    ),
    user:app_users!time_entries_user_id_fkey(
      email,
      first_name,
      last_name
    )
  `);

  // Apply filters
  if (options.ticketId) {
    query = query.eq("ticket_id", options.ticketId);
  }
  if (options.userId) {
    query = query.eq("user_id", options.userId);
  }
  if (options.entryType) {
    query = query.eq("entry_type", options.entryType);
  }
  if (options.isApproved !== undefined) {
    query = query.eq("is_approved", options.isApproved);
  }
  if (options.contactId) {
    // Filter by contact through the tickets relationship
    // We need to use the actual contact_id field from the tickets table
    // For RLS, we need to filter by the ticket's contact_id
    query = query.eq("ticket.contact_id", options.contactId);
  }
  if (options.search) {
    // Search in description and ticket number
    const isNumber = !isNaN(Number(options.search));
    
    if (isNumber) {
      // For numbers, search in description AND exact ticket number match
      query = query.or(`description.ilike.%${options.search}%,ticket.ticket_number.eq.${options.search}`);
    } else {
      // For text, search only in description
      query = query.or(`description.ilike.%${options.search}%`);
    }
  }
  if (options.syncStatus) {
    query = query.eq("sync_status", options.syncStatus);
  }
  if (options.hours) {
    // Filter by hours (exact match for now, could be enhanced for ranges)
    query = query.eq("hours", options.hours);
  }

  // Apply date filtering
  query = applyDateFilter(query, {
    dateField: "date_worked",
    fromDate: options.dateFrom,
    toDate: options.dateTo,
  });

  // Apply ordering
  query = query.order("date_worked", { ascending: false });

  return query;
}

/**
 * Get contact by email (primary or alias)
 */
export function buildContactByEmailQuery(
  supabase: SupabaseClient,
  email: string
) {
  return supabase
    .from("contacts")
    .select("id")
    .or(`email.eq.${email},email_aliases.cs.{"${email}"}`)
    .single();
}

/**
 * Generic query builder with common filters
 */
export class QueryBuilder {
  private query: any;

  constructor(query: any) {
    this.query = query;
  }

  /**
   * Apply date range filter
   */
  dateRange(field: string, from?: Date | null, to?: Date | null): this {
    this.query = applyDateFilter(this.query, {
      dateField: field,
      fromDate: from,
      toDate: to,
    });
    return this;
  }

  /**
   * Apply pagination
   */
  paginate(limit?: number, page?: number): this {
    this.query = applyPagination(this.query, { limit, page });
    return this;
  }

  /**
   * Apply ordering
   */
  orderBy(field: string, ascending = false): this {
    this.query = this.query.order(field, { ascending });
    return this;
  }

  /**
   * Apply limit
   */
  limit(count: number): this {
    this.query = this.query.limit(count);
    return this;
  }

  /**
   * Apply equality filter
   */
  eq(field: string, value: any): this {
    this.query = this.query.eq(field, value);
    return this;
  }

  /**
   * Apply in filter
   */
  in(field: string, values: any[]): this {
    this.query = this.query.in(field, values);
    return this;
  }

  /**
   * Execute the query
   */
  execute() {
    return this.query;
  }

  /**
   * Get the underlying query object
   */
  getQuery() {
    return this.query;
  }
}

/**
 * Create a new query builder
 */
export function createQueryBuilder(query: any): QueryBuilder {
  return new QueryBuilder(query);
}

/**
 * ============================================================================
 * TICKET QUERY BUILDERS
 * ============================================================================
 */

/**
 * Standard ticket fields that include all necessary joins
 * Used by: tickets table, dashboard, most components
 */
export const STANDARD_TICKET_FIELDS = `
  *,
  contacts!inner(
    id,
    name,
    company_name,
    email
  ),
  assigned_user:app_users!fk_tickets_assigned_to(
    id,
    first_name,
    last_name,
    email
  ),
  time_entries(
    id,
    description,
    hours,
    entry_type,
    billable_rate,
    date_worked,
    is_approved,
    created_at
  )
`;

/**
 * Internal/staff-only ticket columns that MUST NOT be exposed to a `client_user`.
 * RLS is row-level and cannot mask columns, so the read query for clients selects
 * an explicit safe allowlist instead of `*`. `customer_visible_notes` is the
 * client-facing note and is intentionally kept.
 *
 * NOTE: this is an app-layer projection, not a server-enforced boundary — a
 * client holding a valid Supabase JWT can still query these columns directly.
 * The real fix belongs in the DB (see the security review for this change).
 */
export const CLIENT_HIDDEN_TICKET_COLUMNS = [
  "internal_notes",
  "hourly_rate",
  "total_cost",
  "total_billed",
  "actual_hours",
  "billable_hours",
  "non_billable_hours",
  "sla_hours",
  "escalation_level",
] as const;

/**
 * Client-safe ticket columns (allowlist). Anything not listed here is hidden
 * from `client_user` reads — so a future internal column is fail-closed (hidden)
 * until explicitly added. Mirrors the ticket read-column set minus
 * CLIENT_HIDDEN_TICKET_COLUMNS; keeps `customer_visible_notes`.
 */
export const CLIENT_SAFE_TICKET_COLUMNS = [
  "id",
  "ticket_number",
  "title",
  "description",
  "contact_id",
  "company_id",
  "company_name",
  "contact_name",
  "is_software",
  "assigned_to",
  "created_by",
  "status",
  "priority",
  "category",
  "estimated_hours",
  "discount_amount",
  "discount_type",
  "discount_percentage",
  "due_date",
  "resolved_at",
  "closed_at",
  "auto_assign",
  "auto_escalate",
  "last_escalation_at",
  "reminder_sent",
  "reminder_sent_at",
  "tags",
  "customer_visible_notes",
  "is_internal",
  "has_time_entries",
  "created_at",
  "updated_at",
  "last_follow_up_task_created_at",
  "last_activity_at",
] as const;

/**
 * Client-safe ticket selection string — the explicit column allowlist plus the
 * same joined relations as STANDARD_TICKET_FIELDS. Used for `client_user` reads
 * (and as a fail-closed default whenever the viewer is not a confirmed employee).
 */
export const CLIENT_SAFE_TICKET_FIELDS = `
  ${CLIENT_SAFE_TICKET_COLUMNS.join(",\n  ")},
  contacts!inner(
    id,
    name,
    company_name,
    email
  ),
  assigned_user:app_users!fk_tickets_assigned_to(
    id,
    first_name,
    last_name,
    email
  ),
  time_entries(
    id,
    description,
    hours,
    entry_type,
    billable_rate,
    date_worked,
    is_approved,
    created_at
  )
`;

/**
 * Minimal ticket fields for search and dropdowns
 * Used by: search, quick selectors
 */
export const MINIMAL_TICKET_FIELDS = `
  id,
  title,
  ticket_number,
  status,
  priority,
  contact_id
`;

/**
 * Report ticket fields for analytics
 * Used by: reports, metrics
 */
export const REPORT_TICKET_FIELDS = `
  id,
  ticket_number,
  title,
  status,
  priority,
  category,
  assigned_to,
  created_at,
  resolved_at,
  closed_at,
  is_internal,
  contact_id,
  actual_hours,
  total_cost,
  total_billed,
  contacts!inner(id, name, company_name),
  assigned_user:app_users!fk_tickets_assigned_to(id, first_name, last_name, email)
`;

/**
 * Metrics ticket fields for counting and aggregation
 * Used by: dashboard stats, metrics
 */
export const METRICS_TICKET_FIELDS = `
  id,
  status,
  priority,
  category,
  created_at,
  resolved_at,
  contact_id,
  assigned_to
`;

export interface TicketQueryParams {
  // Filters
  status?: string | string[];
  priority?: string | string[];
  category?: string | string[];
  assignedTo?: string;
  clientId?: string;
  isInternal?: boolean;
  critical?: boolean;
  
  // Search
  search?: string;
  
  // Date filters
  dateFrom?: string;
  dateTo?: string;
  
  // Pagination
  page?: number;
  limit?: number;
  
  // Sorting
  orderBy?: string;
  ascending?: boolean;
  
  // Client access control
  ticketIds?: string[];
}

export interface TicketQueryOptions {
  fields?: string;
  includeCount?: boolean;
}

/**
 * Build tickets query with comprehensive filtering and relationships
 */
export function buildTicketsQuery(
  supabase: SupabaseClient,
  params: TicketQueryParams = {},
  options: TicketQueryOptions = {}
) {
  const {
    fields = STANDARD_TICKET_FIELDS,
    includeCount = false
  } = options;

  let query = supabase
    .from("tickets")
    .select(fields, { count: includeCount ? "exact" : undefined });

  // Apply filters using the existing QueryBuilder pattern
  const queryBuilder = createQueryBuilder(query);

  // Status filter
  if (params.status) {
    const statusArray = Array.isArray(params.status) 
      ? params.status 
      : params.status.includes(",") 
        ? params.status.split(",") 
        : [params.status];
    
    if (statusArray.length > 1) {
      queryBuilder.in("status", statusArray);
    } else {
      queryBuilder.eq("status", statusArray[0]);
    }
  }

  // Priority filter
  if (params.priority) {
    const priorityArray = Array.isArray(params.priority)
      ? params.priority
      : params.priority.includes(",")
        ? params.priority.split(",")
        : [params.priority];
    
    if (priorityArray.length > 1) {
      queryBuilder.in("priority", priorityArray);
    } else {
      queryBuilder.eq("priority", priorityArray[0]);
    }
  }

  // Category filter
  if (params.category) {
    const categoryArray = Array.isArray(params.category)
      ? params.category
      : params.category.includes(",")
        ? params.category.split(",")
        : [params.category];
    
    if (categoryArray.length > 1) {
      queryBuilder.in("category", categoryArray);
    } else {
      queryBuilder.eq("category", categoryArray[0]);
    }
  }

  // Assigned to filter
  if (params.assignedTo) {
    queryBuilder.eq("assigned_to", params.assignedTo);
  }

  // Client ID filter
  if (params.clientId) {
    queryBuilder.eq("contact_id", params.clientId);
  }

  // Internal filter
  if (params.isInternal !== undefined) {
    queryBuilder.eq("is_internal", params.isInternal);
  }

  // Critical filter (priority + status)
  if (params.critical) {
    queryBuilder.in("priority", ["critical", "urgent"]);
    queryBuilder.in("status", ["new", "assigned", "in_progress"]);
  }

  // Search filter
  if (params.search) {
    const isNumber = !isNaN(Number(params.search));
    
    if (isNumber) {
      // For numbers, search in title, description, AND exact ticket number match
      queryBuilder.getQuery().or(
        `title.ilike.%${params.search}%,description.ilike.%${params.search}%,ticket_number.eq.${params.search}`
      );
    } else {
      // For text, search only in title and description
      queryBuilder.getQuery().or(
        `title.ilike.%${params.search}%,description.ilike.%${params.search}%`
      );
    }
  }

  // Date filters
  if (params.dateFrom || params.dateTo) {
    queryBuilder.dateRange(
      "created_at", 
      params.dateFrom ? new Date(params.dateFrom) : null, 
      params.dateTo ? new Date(params.dateTo) : null
    );
  }

  // Pagination
  if (params.limit) {
    queryBuilder.paginate(params.limit, params.page);
  }

  // Ticket IDs filter (for client access control)
  if (params.ticketIds && params.ticketIds.length > 0) {
    queryBuilder.in("id", params.ticketIds);
  }

  // Apply sorting
  const orderBy = params.orderBy || "created_at";
  const ascending = params.ascending ?? false;
  queryBuilder.orderBy(orderBy, ascending);

  return queryBuilder.execute();
}

/**
 * Quick functions for common ticket queries
 */

/**
 * Get tickets with full data and processing
 */
export function buildStandardTicketsQuery(
  supabase: SupabaseClient,
  params: TicketQueryParams = {},
  options: { clientSafe?: boolean } = {}
) {
  return buildTicketsQuery(supabase, params, {
    fields: options.clientSafe ? CLIENT_SAFE_TICKET_FIELDS : STANDARD_TICKET_FIELDS,
    includeCount: false
  });
}

/**
 * Get tickets with pagination info
 */
export function buildPaginatedTicketsQuery(
  supabase: SupabaseClient,
  params: TicketQueryParams = {},
  options: { clientSafe?: boolean } = {}
) {
  return buildTicketsQuery(supabase, params, {
    fields: options.clientSafe ? CLIENT_SAFE_TICKET_FIELDS : STANDARD_TICKET_FIELDS,
    includeCount: true
  });
}

/**
 * Get minimal ticket data for search
 */
export function buildMinimalTicketsQuery(
  supabase: SupabaseClient,
  params: TicketQueryParams = {}
) {
  return buildTicketsQuery(supabase, params, {
    fields: MINIMAL_TICKET_FIELDS,
    includeCount: false
  });
}

/**
 * Get tickets for reports
 */
export function buildReportTicketsQuery(
  supabase: SupabaseClient,
  params: TicketQueryParams = {}
) {
  return buildTicketsQuery(supabase, params, {
    fields: REPORT_TICKET_FIELDS,
    includeCount: false
  });
}

/**
 * Get tickets for metrics
 */
export function buildMetricsTicketsQuery(
  supabase: SupabaseClient,
  params: TicketQueryParams = {}
) {
  return buildTicketsQuery(supabase, params, {
    fields: METRICS_TICKET_FIELDS,
    includeCount: false
  });
}

/**
 * Get critical tickets
 */
export function buildCriticalTicketsQuery(
  supabase: SupabaseClient,
  ticketIds?: string[]
) {
  return buildTicketsQuery(supabase, {
    critical: true,
    ticketIds,
    limit: 10
  }, {
    fields: STANDARD_TICKET_FIELDS,
    includeCount: false
  });
}

/**
 * ============================================================================
 * CONTACT QUERY BUILDERS
 * ============================================================================
 */

export interface ContactQueryParams {
  // Filters
  status?: string;
  contactType?: string;
  isActive?: boolean;
  
  // Search
  search?: string;
  
  // Pagination
  page?: number;
  limit?: number;
  
  // Sorting
  orderBy?: string;
  ascending?: boolean;
}

/**
 * Canonical PostgREST select for loading a contact together with the linked
 * company columns the email-template variable engine reads ({{company_name}},
 * {{industry}}, {{website}}, {{contract_type}}, {{hourly_rate}},
 * {{monthly_rate}}, {{payment_terms}}). This is the SINGLE source of truth for
 * that embed — every surface that hydrates a contact for template resolution
 * (compose, sequences, Slack send-email, Meridian template tool, marketing
 * campaigns) must use it so `contact.companies` is populated consistently.
 * Keep in sync with `ContactTemplateSource` in the hub template-variables
 * mappers (the Pick<> that types the same columns).
 *
 * The embed NAMES ITS FOREIGN KEY (`companies!contacts_company_id_fkey`) and
 * must keep doing so: `contacts` and `companies` reference each other TWICE —
 * `contacts.company_id -> companies.id` (the person's employer, what we want)
 * and `companies.line_contact_id -> contacts.id` (the shared-line anchor, see
 * CONTACT_METHOD_EXCLUSIVITY). A bare `companies(...)` is therefore ambiguous
 * and PostgREST rejects the whole query with PGRST201 "Could not embed because
 * more than one relationship was found for 'contacts' and 'companies'" — which
 * is exactly how the contact Communication-history card started erroring. The
 * response key stays `companies`, so consumers are unaffected.
 */
export const CONTACT_TEMPLATE_SELECT =
  "*, companies!contacts_company_id_fkey(name, industry, website, contract_type, hourly_rate, monthly_rate, payment_terms)";

/**
 * Build contacts query with filtering and relationships
 */
export function buildContactsQuery(
  supabase: SupabaseClient,
  params: ContactQueryParams = {}
) {
  // Embed the linked company (CONTACT_TEMPLATE_SELECT) so template-variable
  // resolution can fall back to companies.* when the denormalized
  // contacts.company_name is blank/stale.
  let query = supabase.from("contacts").select(CONTACT_TEMPLATE_SELECT);

  // Apply filters using the existing QueryBuilder pattern
  const queryBuilder = createQueryBuilder(query);

  // Status filter
  if (params.status) {
    queryBuilder.eq("status", params.status);
  }

  // Contact type filter
  if (params.contactType) {
    queryBuilder.eq("contact_type", params.contactType);
  }

  // Active filter
  if (params.isActive !== undefined) {
    queryBuilder.eq("is_active", params.isActive);
  }

  // Search filter
  if (params.search) {
    queryBuilder.getQuery().or(
      `name.ilike.%${params.search}%,company_name.ilike.%${params.search}%,email.ilike.%${params.search}%,email_aliases.cs.{"*${params.search}*"}`
    );
  }

  // Pagination
  if (params.limit) {
    queryBuilder.paginate(params.limit, params.page);
  }

  // Apply sorting
  const orderBy = params.orderBy || "created_at";
  const ascending = params.ascending ?? false;
  queryBuilder.orderBy(orderBy, ascending);

  return queryBuilder.execute();
}

/**
 * ============================================================================
 * USER QUERY BUILDERS
 * ============================================================================
 */

export interface UserQueryParams {
  // Filters
  isActive?: boolean | null;
  userType?: string | null;
  
  // Search
  search?: string;
  
  // Pagination
  page?: number;
  limit?: number;
  
  // Sorting
  orderBy?: string;
  ascending?: boolean;
}

/**
 * Build users query with filtering
 */
export function buildUsersQuery(
  supabase: SupabaseClient,
  params: UserQueryParams = {}
) {
  let query = supabase
    .from("app_users")
    .select("*");

  // Apply filters using the existing QueryBuilder pattern
  const queryBuilder = createQueryBuilder(query);

  // Active filter
  if (params.isActive !== undefined && params.isActive !== null) {
    queryBuilder.eq("is_active", params.isActive);
  }

  // User type filter
  if (params.userType) {
    queryBuilder.eq("role", params.userType);
  }

  // Search filter
  if (params.search) {
    queryBuilder.getQuery().or(
      `first_name.ilike.%${params.search}%,last_name.ilike.%${params.search}%,email.ilike.%${params.search}%`
    );
  }

  // Pagination
  if (params.limit) {
    queryBuilder.paginate(params.limit, params.page);
  }

  // Apply sorting
  const orderBy = params.orderBy || "created_at";
  const ascending = params.ascending ?? false;
  queryBuilder.orderBy(orderBy, ascending);

  return queryBuilder.execute();
}

/**
 * Build all users query (for dropdowns, etc.)
 */
export function buildAllUsersQuery(
  supabase: SupabaseClient,
  orderBy: string = "first_name",
  ascending: boolean = true
) {
  let query = supabase
    .from("app_users")
    .select("*");

  return query.order(orderBy, { ascending });
}

/**
 * ============================================================================
 * CONSOLIDATED TYPES - ELIMINATE DUPLICATES
 * ============================================================================
 */

/**
 * ConnectionShare interface - consolidated from duplicate definitions
 * Previously duplicated in:
 * - apps/hub/components/crm/integrations/ShareConnectionModal.tsx
 * - apps/hub/lib/hooks/api/connection-sharing.ts
 */
export interface ConnectionShare {
  id: string;
  shared_with_user_id: string;
  created_at: string;
  /** Whether this share also grants DWD-impersonated Drive access to the owner. Default true. */
  drive_access_enabled: boolean;
  app_users: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
}

/**
 * AppUser interface - consolidated from duplicate definitions
 * Previously duplicated in:
 * - apps/hub/components/shared/UserSelect.tsx
 * - apps/hub/lib/types/unified.ts
 * - apps/hub/lib/types/user.ts
 * 
 * Based on AppUser from database types to ensure compatibility
 */
export interface AppUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  role_id?: string;
  created_at?: string;
  updated_at?: string;
  available_hours?: number;
  is_billable?: boolean;
  hourly_rate?: number;
}

/**
 * UserPreferences interface - consolidated from multiple versions
 * Previously defined differently in:
 * - apps/hub/lib/types/unified.ts (comprehensive)
 * - apps/hub/lib/preferences-context.tsx (simple)
 * - apps/hub/lib/types/user.ts (extended)
 */
export interface UserPreferences {
  // Core preferences (from unified.ts)
  defaultInbox?: string;
  autoSyncOnLogin?: boolean;
  showSlackAsThreads?: boolean;
  combineEmailSlack?: boolean;
  notifyNewMessages?: boolean;
  notifyDailyDigest?: boolean;
  emailFrequency?: "off" | "daily" | "weekly";
  theme?: "light" | "dark" | "system";
  
  // Additional preferences (from user.ts)
  language?: string;
  timezone?: string;
  notifications?: {
    email?: boolean;
    push?: boolean;
    sms?: boolean;
  };
  dashboard?: {
    defaultView?: string;
    showWidgets?: string[];
  };
  
  // Simple preferences (from preferences-context.tsx)
  hideFAQ?: boolean;
  goMode?: boolean;
}
